import { toastBus } from "@/components/ui/toast-provider"

/**
 * Public toast API. Every callsite in the app uses this — never call
 * the underlying provider/bus directly.
 *
 * Each method returns the toast id so you can later replace or
 * dismiss it explicitly:
 *
 *   const id = Toast.loading("Cancelling job…")
 *   // …work…
 *   Toast.success("Job cancelled", undefined, { id })  // replaces in place
 *   Toast.dismiss(id)                                  // or just dismiss
 *
 * Replacement-by-id is rock solid here because the provider owns the
 * React state — there's no third-party queue to fight.
 */

type Opts = { id?: string; duration?: number | null }

function show(
  type: "success" | "error" | "info" | "loading",
  title: string,
  description?: string,
  opts?: Opts,
): string {
  const id = opts?.id ?? newId()
  toastBus.emit({
    kind: "show",
    toast: {
      id,
      type,
      title,
      description,
      duration: opts?.duration,
    },
  })
  return id
}

function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `t-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
}

export const Toast = {
  success: (title: string, description?: string, opts?: Opts) =>
    show("success", title, description, opts),

  error: (title: string, description?: string, opts?: Opts) =>
    show("error", title, description, opts),

  info: (title: string, description?: string, opts?: Opts) =>
    show("info", title, description, opts),

  /**
   * Loading toast that stays until you replace it (Toast.success/error
   * with `{ id }`) or dismiss it explicitly. Has a 30s safety cap.
   */
  loading: (title: string, description?: string) =>
    show("loading", title, description),

  dismiss: (id?: string) => toastBus.emit({ kind: "dismiss", id }),
}
